import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { EXPERIMENT_FLAGS, type OnboardingVariant } from '@scio3/shared-types';
import { SET_EXPERIMENT_VARIANT, type SetExperimentVariantResponse } from '@scio3/graphql-client';
import { isPosthogEnabled, posthog } from '@/lib/posthog';
import { useAuthStore } from '@/stores/auth-store';
import { readQaOverride } from '../utils/qa-override';

/**
 * Safety net for the pathological "SDK loaded but never answers" case. Kept
 * generous so a slow-but-working flag load is never pre-empted by a premature
 * control answer (which would flash the survey over the screens for treatment).
 */
const FLAG_SAFETY_TIMEOUT_MS = 8000;

/** Retry a "No matching condition set" result this many times before accepting
 *  control — the cohort props (signup_date/signup_source) may still be
 *  propagating to the /flags request right after signup (esp. social). */
const MAX_FLAG_RETRIES = 3;
const FLAG_RETRY_DELAY_MS = 1000;

// The 3-arm test reuses the existing scio3b-split flag (now 30/30/40 with scio3c
// added) so already-assigned scio3a/scio3b users stay pinned via
// company.experiments['scio3b-split'] and never re-bucket. New signups get the
// 30/30/40 split.
const FLAG = EXPERIMENT_FLAGS.SCIO3B_SPLIT;

function normalizeVariant(value: unknown): OnboardingVariant | null {
  return value === 'scio3a' || value === 'scio3b' || value === 'scio3c' ? value : null;
}

// Per-page-load guard so the experiment-exposure event fires at most once per
// user, no matter how many components mount this hook.
let exposedFor: string | null = null;

/**
 * Resolve the user's variant for the scio3b-split A/B/C test, backend-first.
 *
 * Order of precedence: QA override → the value stored on the company → the first
 * IDENTIFIED PostHog answer (which is then persisted to the company).
 *
 * - **Company has it** → return it synchronously from the auth-store (this is what
 *   makes the assignment sticky AND makes every consumer agree — they all read one
 *   field, so the nav and the checklist can never disagree, and it never flips).
 * - **Company doesn't have it (first time ever)** → wait for an IDENTIFIED PostHog
 *   answer (`_isIdentified()` and `distinct_id === userId`), then call
 *   `setExperimentVariant` (write-once on the backend) AND optimistically patch the
 *   company in-store so the rest of this session is consistent too.
 * - No SDK / 8s timeout → stays unresolved (`variant: null`, nothing persisted) so a
 *   slow PostHog load never locks a user into the wrong bucket — next login
 *   re-resolves. Consumers read control-ish behavior via useIsScio3b/cVariant().
 *
 * Resolution lives here (not in AuthProvider) so it runs during the signup flow
 * too — AuthProvider's `loadUserContext` early-returns while `signupInProgress`.
 */
export function useOnboardingVariant(): {
  variant: OnboardingVariant | null;
  isLoading: boolean;
} {
  // Read override once on mount — subsequent ?force_variant= changes only take
  // effect on full page reload, matching how PostHog assigns variants.
  const [override] = useState<OnboardingVariant | null>(() => readQaOverride());
  const company = useAuthStore((s) => s.company);
  const setCompany = useAuthStore((s) => s.setCompany);
  const userId = useAuthStore((s) => s.me?._id);

  // Source of truth: the variant stored on the company. Reactive — once one
  // consumer resolves + persists it (below), every consumer reads it here.
  const storedVariant = normalizeVariant(company?.experiments?.[FLAG]);
  const accountId = company?._id;

  const [setExperimentVariant] = useMutation<SetExperimentVariantResponse>(SET_EXPERIMENT_VARIANT);

  // Session-locked fallback for the very first time, before the backend value
  // exists. Seeded from override / stored value so returning companies never
  // see the loading skeleton.
  const [locked, setLocked] = useState<OnboardingVariant | null>(
    () => override ?? normalizeVariant(useAuthStore.getState().company?.experiments?.[FLAG]),
  );

  useEffect(() => {
    if (override || locked || !accountId) return; // already resolved

    // No SDK (no key / embedded iframe) → control; don't sit on the skeleton.
    if (!isPosthogEnabled) {
      setLocked(null);
      return;
    }

    let done = false;
    let retriesLeft = MAX_FLAG_RETRIES;
    let retryTimer: number | undefined;
    // Set once we've forced a fresh post-identify flags reload — see accept().
    let forcedReload = false;

    // Persist a resolved variant: write-once on the backend, and optimistically
    // patch the company in-store so every other consumer reads the SAME value
    // immediately (no first-session divergence between nav and checklist).
    const persist = async (value: OnboardingVariant) => {
      const current = useAuthStore.getState().company;
      if (current && !current.experiments?.[FLAG]) {
        setCompany({
          ...current,
          experiments: { ...(current.experiments ?? {}), [FLAG]: value },
        });
      }
      try {
        await setExperimentVariant({ variables: { key: FLAG, variant: value } });
      } catch {
        // Best-effort — the optimistic store value keeps this session consistent;
        // next login re-resolves and re-persists.
      }
    };

    // Accept ONLY an identified answer, so we never persist the anonymous bucket.
    // Distinguish a real cohort assignment ('scio3a' | 'scio3b') from "No matching
    // condition set" (false/undefined): the latter usually means signup_date /
    // signup_source haven't reached the /flags request yet (race on the fast
    // social-signup navigate), so retry a few fresh evaluations before giving up
    // and treating the user as control.
    const accept = () => {
      if (done) return;
      // Must be identified as THIS user first — otherwise distinct_id is still the
      // anonymous one (or mid-transition) and any flag value is bucketed against it.
      if (!posthog._isIdentified?.() || !userId || posthog.get_distinct_id?.() !== userId) return;

      // Being identified is NOT enough. The flag values from the pre-identify
      // (anonymous) /flags request linger in the SDK even after distinct_id flips
      // to userId, so getFeatureFlag() here can still return the ANONYMOUS bucket.
      // That is the social-signup bug: the anonymous id hashes to scio3a while the
      // identified person hashes to scio3b — accepting now would lock + persist
      // scio3a and fire a scio3a exposure for a user PostHog considers scio3b.
      // Force exactly one reload now that we're identified (cohort person-props
      // are already registered in analyticsIdentify) and only trust the value its
      // onFeatureFlags callback delivers — that request is bucketed against userId.
      if (!forcedReload) {
        forcedReload = true;
        posthog.reloadFeatureFlags?.();
        return;
      }

      const raw = posthog.getFeatureFlag(FLAG);
      if (raw === 'scio3a' || raw === 'scio3b' || raw === 'scio3c') {
        done = true;
        window.clearTimeout(retryTimer);
        setLocked(raw);
        void persist(raw);
        return;
      }

      // No cohort match (yet). Re-evaluate after a short delay so a propagating
      // signup_date can land in the next /flags request.
      if (retriesLeft > 0) {
        retriesLeft -= 1;
        console.warn(`[experiment] ${FLAG}: no cohort match — retrying (${retriesLeft} left).`);
        retryTimer = window.setTimeout(() => posthog.reloadFeatureFlags?.(), FLAG_RETRY_DELAY_MS);
        return;
      }

      // Retries exhausted → treat as control, but do NOT persist, so a genuine
      // race victim re-resolves on the next login instead of being locked.
      done = true;
      window.clearTimeout(retryTimer);
      setLocked(null);
    };

    // Do NOT call accept() synchronously: on the fast social-signup navigate the
    // hook mounts before PostHog finishes loading flags, so getFeatureFlag would
    // return undefined → default to scio3a, set done=true, and block the real
    // loaded read (no $feature_flag_called, wrong control lock). onFeatureFlags
    // fires synchronously when flags are ALREADY loaded, so the already-loaded
    // case is still handled here — just without the unloaded-undefined race.
    const unsub = posthog.onFeatureFlags?.((flags, variants, info) => {
      // Diagnostic: visible in DevTools so you can tell whether PostHog answered,
      // what variant it assigned, and whether the fire was identified.
      console.warn(`[experiment] ${FLAG}: PostHog responded.`, {
        assignedVariant: variants?.[FLAG] ?? '(none — control)',
        identified: posthog._isIdentified?.(),
        distinctId: posthog.get_distinct_id?.(),
        expectedUserId: userId,
        totalActiveFlags: flags.length,
        errorsLoading: info?.errorsLoading,
      });
      accept();
    });

    const timeoutId = window.setTimeout(() => {
      if (done) return;
      done = true;
      // Timeout fallback is NOT persisted — a slow PostHog load must never lock a
      // user into control. They re-resolve next login.
      console.error(
        `[experiment] ${FLAG}: no identified PostHog answer within ${FLAG_SAFETY_TIMEOUT_MS}ms — falling back to control (scio3a). ` +
          `Check that VITE_POSTHOG_KEY is set and VITE_POSTHOG_API_HOST is reachable.`,
      );
      setLocked(null);
    }, FLAG_SAFETY_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timeoutId);
      window.clearTimeout(retryTimer);
      unsub?.();
    };
  }, [override, storedVariant, locked, userId, accountId, setCompany, setExperimentVariant]);

  // Pin PostHog's flag to the resolved (DB) variant via the `ff_scio3b_split`
  // person property — this is what the flag is DESIGNED for: its Sets 1–3 force
  // the arm matching `ff_scio3b_split` (== scio3a/scio3b/scio3c), and Set 4 is the
  // 30/30/40 splitter for brand-new signups. Without this property set, every
  // eval falls through to Set 4 and re-rolls (anon-vs-identified hash + signup_date
  // timing) → the variant flips across logins. Setting it makes PostHog return the
  // same arm every time, on every device, matching company.experiments.
  //
  // Also emit a one-per-user exposure event so analysis can break down by
  // `variant` independently of PostHog's bucketing. QA `?force_variant=` overrides
  // are excluded — a forced variant must never persist onto the real person.
  const pinVariant = storedVariant ?? locked;
  useEffect(() => {
    if (!pinVariant || !userId || !isPosthogEnabled) return;
    if (exposedFor === userId) return;
    exposedFor = userId;
    // setPersonProperties → persists server-side (sticks across sessions/devices).
    // setPersonPropertiesForFlags(…, true) → feeds THIS session's /flags request
    // and reloads, so the current page reflects the pin immediately.
    posthog.setPersonProperties({ ff_scio3b_split: pinVariant });
    posthog.setPersonPropertiesForFlags({ ff_scio3b_split: pinVariant }, true);
    posthog.capture('onboarding_variant_exposure', { experiment: FLAG, variant: pinVariant });
  }, [pinVariant, userId]);

  if (override) return { variant: override, isLoading: false };
  if (storedVariant) return { variant: storedVariant, isLoading: false };
  if (locked) return { variant: locked, isLoading: false };
  // Still resolving the first identified answer → keep the skeleton.
  return { variant: null, isLoading: true };
}

/**
 * Stable boolean check — useful for `if`-style branching outside JSX without
 * also pulling `isLoading`. During the loading window, returns `false` so
 * callers don't accidentally render scio3b UI before the variant resolves.
 */
export function useIsScio3bVariant(): boolean {
  const { variant, isLoading } = useOnboardingVariant();
  return !isLoading && variant === 'scio3b';
}

/**
 * Stable boolean check for the scio3c arm (treatment-minus-survey, which instead
 * gets the edit-screen tutorial). Returns `false` while the variant is still
 * resolving so the tutorial never flashes before bucketing is known.
 */
export function useIsScio3cVariant(): boolean {
  const { variant, isLoading } = useOnboardingVariant();
  return !isLoading && variant === 'scio3c';
}

/**
 * One-shot side-effect: registers `onboarding_variant` as a PostHog person
 * property once the variant resolves, so every subsequent event auto-attaches
 * it without callers needing to thread the prop through.
 *
 * Mount once near the variant decision point (e.g. inside `OnboardingRouter`).
 */
export function useRegisterVariantProperty(
  variant: OnboardingVariant | null,
  isLoading: boolean,
): void {
  useEffect(() => {
    if (isLoading || !variant) return;
    // Lazy-import the singleton so we don't pull posthog into modules that only
    // need the variant value (e.g. tests).
    import('@/lib/posthog')
      .then(({ posthog }) => {
        posthog.register({ onboarding_variant: variant });
      })
      .catch(() => {
        // Posthog optional — swallow.
      });
  }, [variant, isLoading]);
}
